/**
 * The one composer every agent chat in Omni uses: write, dictate, call, attach files.
 *
 *   text      Enter sends, Shift+Enter is a new line
 *   voice     the mic dictates into the box (the browser's own speech recognition: only the text leaves the device)
 *   call      a spoken conversation with the agent; while it runs the box becomes the call bar (who talks, mute, end)
 *   files     PDF, images, video, documents: the paperclip, drag and drop onto the box, or paste. Attachments show
 *             as chips (pictures as thumbnails) and go to the agent with its own upload path (`upload`, or `onSend`)
 *
 * The owner decides what a message does (`onSend`) and how a call runs (`call`); the composer is only the input.
 * Just an input: no suggestion chips (house rule). Colours come from the shell's tokens (light and dark) with the
 * light values as fallbacks, so the composer looks the same in the classic layout.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { ArrowUp, AudioLines, File as FileIcon, FileText, Film, Image as ImageIcon, Loader2, Mic, MicOff, Paperclip, Phone, PhoneOff, Plus, Square, X } from 'lucide-react';
import { cn } from '@/components/ui/utils';

export type FileKind = 'pdf' | 'image' | 'video' | 'doc' | 'other';
export interface ComposerFile { key: string; file: File; name: string; kind: FileKind; preview?: string; url?: string; failed?: boolean }

export type CallPhase = 'off' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'working' | 'error';
export interface ComposerCall {
  phase: CallPhase;
  /** One line under the title: what she is saying, what was heard, or the error. */
  caption?: string;
  muted?: boolean;
  start: () => void;
  end: () => void;
  mute?: (muted: boolean) => void;
  /** Hidden when the agent has no call here (the button is left out, not disabled). */
  available?: boolean;
}

export interface ComposerMenuItem { icon: typeof Plus; label: string; hint?: string; run: () => void }

export interface AgentComposerHandle { focus: () => void; setText: (t: string) => void; addFiles: (files: File[]) => void }

export interface AgentComposerProps {
  /** Send the message. Return false to keep the draft (nothing was sent). */
  onSend: (text: string, files: ComposerFile[]) => void | boolean | Promise<void | boolean>;
  agentName?: string;
  placeholder?: string;
  busy?: boolean;
  /** While busy: a stop button instead of send. */
  onStop?: () => void;
  /** Files: on by default. `accept` for the picker, `acceptFile` to drop what this agent cannot read. */
  allowFiles?: boolean;
  accept?: string;
  acceptFile?: (f: File) => boolean;
  maxFiles?: number;
  /** Upload at once (pictures are previewed while they go up): one url per file, null when it failed. */
  upload?: (files: File[]) => Promise<Array<string | null>>;
  /** Files may be sent without text (a brief dropped on Lykke). */
  filesAlone?: boolean;
  call?: ComposerCall | null;
  /** Extra entries in a + menu beside the paperclip (the paperclip is always there when files are on). */
  menu?: ComposerMenuItem[];
  /** Above the box: a mark on a slide, a draft in focus. */
  chips?: ReactNode;
  autoFocus?: boolean;
  disabled?: boolean;
  /** The bigger box (a home or an empty conversation). */
  size?: 'panel' | 'hero';
  className?: string;
  /** Allowed by the owner while the agent is busy (a deck's "Stop" sends nothing else). */
  sendWhileBusy?: boolean;
}

type Rec = { lang: string; interimResults: boolean; continuous: boolean; onresult: ((e: any) => void) | null; onend: (() => void) | null; onerror: (() => void) | null; start: () => void; stop: () => void };
const recCtor = (): (new () => Rec) | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: new () => Rec; webkitSpeechRecognition?: new () => Rec };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

// eslint-disable-next-line react-refresh/only-export-components -- tiny helper shared with the tests
export function fileKind(f: { name: string; type: string }): FileKind {
  if (/^image\//.test(f.type) || /\.(png|jpe?g|gif|webp|heic|svg)$/i.test(f.name)) return 'image';
  if (/^video\//.test(f.type) || /\.(mp4|mov|webm|m4v)$/i.test(f.name)) return 'video';
  if (/pdf/.test(f.type) || /\.pdf$/i.test(f.name)) return 'pdf';
  if (/msword|officedocument|text\/|rtf|presentation|spreadsheet|csv/.test(f.type) || /\.(docx?|pptx?|xlsx?|txt|md|rtf|csv|key|pages)$/i.test(f.name)) return 'doc';
  return 'other';
}
const KIND_ICON: Record<FileKind, typeof FileIcon> = { pdf: FileText, doc: FileText, image: ImageIcon, video: Film, other: FileIcon };
export const DEFAULT_ACCEPT = 'image/*,video/*,application/pdf,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.rtf,.csv,text/*';

const CALL_TITLE: Record<CallPhase, string> = { off: '', connecting: 'Calling', listening: 'Listening', thinking: 'On it', speaking: 'Talking', working: 'Working on it', error: 'Call problem' };

export const AgentComposer = forwardRef<AgentComposerHandle, AgentComposerProps>(function AgentComposer({
  onSend, agentName = 'Lykke', placeholder, busy = false, onStop, allowFiles = true, accept = DEFAULT_ACCEPT, acceptFile, maxFiles = 10,
  upload, filesAlone = false, call, menu = [], chips, autoFocus, disabled, size = 'panel', className, sendWhileBusy = false,
}, ref) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<ComposerFile[]>([]);
  const [drag, setDrag] = useState(false);
  const [plus, setPlus] = useState(false);
  const [dictating, setDictating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const rec = useRef<Rec | null>(null);
  const filesRef = useRef(files); filesRef.current = files;
  const hero = size === 'hero';
  const canDictate = !!recCtor();

  useEffect(() => () => { for (const f of filesRef.current) if (f.preview) URL.revokeObjectURL(f.preview); try { rec.current?.stop(); } catch { /* not running */ } }, []);
  useEffect(() => { if (autoFocus) setTimeout(() => box.current?.focus(), 30); }, [autoFocus]);
  // A new line grows the box up to a limit.
  useEffect(() => {
    const el = box.current; if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(hero ? 220 : 168, Math.max(hero ? 52 : 24, el.scrollHeight))}px`;
  }, [text, hero]);

  const addFiles = useCallback(async (list: File[]) => {
    if (!allowFiles || !list.length) return;
    const room = Math.max(0, maxFiles - filesRef.current.length);
    const ok = list.filter((f) => (acceptFile ? acceptFile(f) : true));
    const refused = list.length - ok.length;
    const take = ok.slice(0, room);
    setNotice(refused ? `${agentName} cannot read ${refused === 1 ? 'that kind of file' : 'some of those files'} here.` : ok.length > room ? `Up to ${maxFiles} files at a time.` : null);
    if (!take.length) return;
    const fresh: ComposerFile[] = take.map((f, i) => {
      const kind = fileKind(f);
      return { key: `${Date.now()}-${i}-${f.name}`, file: f, name: f.name || `file-${i + 1}`, kind, preview: kind === 'image' ? URL.createObjectURL(f) : undefined };
    });
    setFiles((cur) => [...cur, ...fresh]);
    if (!upload) return;
    try {
      const urls = await upload(take);
      setFiles((cur) => cur.map((c) => { const j = fresh.findIndex((x) => x.key === c.key); return j < 0 ? c : urls[j] ? { ...c, url: urls[j]! } : { ...c, failed: true }; }));
    } catch {
      setFiles((cur) => cur.map((c) => (fresh.some((x) => x.key === c.key) ? { ...c, failed: true } : c)));
    }
  }, [acceptFile, agentName, allowFiles, maxFiles, upload]);

  useImperativeHandle(ref, () => ({ focus: () => box.current?.focus(), setText: (t: string) => setText(t), addFiles: (fs: File[]) => void addFiles(fs) }), [addFiles]);

  const drop = (key: string) => setFiles((cur) => { const f = cur.find((x) => x.key === key); if (f?.preview) URL.revokeObjectURL(f.preview); return cur.filter((x) => x.key !== key); });
  const uploading = !!upload && files.some((f) => !f.url && !f.failed);
  const failed = files.some((f) => f.failed);
  const hasText = !!text.trim();
  const canSend = !disabled && (!busy || sendWhileBusy) && !uploading && !failed && (hasText || (filesAlone && files.length > 0));

  const send = async () => {
    if (!canSend) return;
    const t = text; const fs = files;
    try { rec.current?.stop(); } catch { /* not running */ }
    setText(''); setFiles([]); setNotice(null);
    const r = await onSend(t.trim(), fs);
    if (r === false) { setText(t); setFiles(fs); return; }
    for (const f of fs) if (f.preview) URL.revokeObjectURL(f.preview);
  };

  const toggleDictation = () => {
    const Ctor = recCtor(); if (!Ctor) return;
    if (dictating) { try { rec.current?.stop(); } catch { /* stopped */ } return; }
    const r = new Ctor();
    r.lang = navigator.language || 'en-GB'; r.interimResults = true; r.continuous = true;
    const base = text.trim() ? `${text.trim()} ` : '';
    r.onresult = (e: any) => { let said = ''; for (let i = 0; i < e.results.length; i++) said += e.results[i][0].transcript; setText(base + said); };
    r.onend = () => { setDictating(false); rec.current = null; };
    r.onerror = () => { setDictating(false); rec.current = null; setNotice('The microphone is not available: check the browser permission.'); };
    rec.current = r; setDictating(true);
    try { r.start(); } catch { setDictating(false); rec.current = null; }
  };


  const callOn = !!call && call.phase !== 'off';
  const iconBtn = 'grid h-8 w-8 shrink-0 place-items-center rounded-full text-[var(--shell-muted,#667085)] transition-colors hover:bg-[var(--shell-hover,#f2f4f7)] hover:text-[var(--shell-text,#101828)] disabled:opacity-40';

  if (callOn && call) {
    const live = call.phase === 'speaking' || call.phase === 'listening';
    return (
      <div className={cn('rounded-2xl border border-[var(--shell-border,#eaecf0)] bg-[var(--shell-input,#fff)] p-3 shadow-[0_2px_10px_rgba(16,24,40,0.06)]', className)} data-agent-composer data-call={call.phase}>
        <div className="flex items-center gap-3">
          <span className="relative grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#be43ff]/12">
            {live && <span className={cn('absolute inset-0 rounded-full bg-[#be43ff]/25', call.phase === 'speaking' && 'animate-ping')} />}
            {call.phase === 'connecting' ? <Loader2 className="relative h-4 w-4 animate-spin text-[#7a1fbf]" /> : <AudioLines className="relative h-4 w-4 text-[#7a1fbf]" />}
          </span>
          <div className="min-w-0 flex-1" aria-live="polite">
            <div className="text-[13px] font-semibold text-[var(--shell-text,#101828)]">{call.muted ? 'Muted' : `${CALL_TITLE[call.phase]}${call.phase === 'connecting' ? ` ${agentName}` : ''}`}</div>
            <div className="truncate text-[12px] text-[var(--shell-muted,#667085)]">{call.caption || 'Just talk: she hears you and answers.'}</div>
          </div>
          {call.mute && call.phase !== 'connecting' && call.phase !== 'error' && (
            <button type="button" onClick={() => call.mute?.(!call.muted)} aria-pressed={!!call.muted} aria-label={call.muted ? 'Unmute' : 'Mute'} title={call.muted ? 'Unmute' : 'Mute'}
              className={cn('grid h-9 w-9 place-items-center rounded-full', call.muted ? 'bg-[var(--shell-text,#101828)] text-[var(--shell-surface,#fff)]' : 'bg-[var(--shell-hover,#f2f4f7)] text-[var(--shell-text,#344054)]')}>
              {call.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          )}
          <button type="button" onClick={call.end} aria-label="End call" title="End call" className="grid h-9 w-9 place-items-center rounded-full bg-[#d92d20] text-white hover:bg-[#b42318]"><PhoneOff className="h-4 w-4" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative', className)} data-agent-composer>
      {chips && <div className="mb-1.5 flex flex-wrap items-center gap-1.5">{chips}</div>}
      <div
        className={cn('relative rounded-2xl border bg-[var(--shell-input,#fff)] transition-colors focus-within:border-[#be43ff] focus-within:shadow-[0_0_0_4px_rgba(190,67,255,0.08)]',
          drag ? 'border-dashed border-[#be43ff]' : 'border-[var(--shell-border-strong,#d0d5dd)]')}
        onDragEnter={(e) => { if (allowFiles && Array.from(e.dataTransfer.types).includes('Files')) { e.preventDefault(); setDrag(true); } }}
        onDragOver={(e) => { if (allowFiles && Array.from(e.dataTransfer.types).includes('Files')) e.preventDefault(); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDrag(false); }}
        onDrop={(e) => { setDrag(false); const fs = Array.from(e.dataTransfer.files || []); if (fs.length) { e.preventDefault(); void addFiles(fs); } }}
      >
        {drag && <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-2xl bg-[#be43ff]/6 text-[13px] font-semibold text-[#7a1fbf]">Drop files for {agentName}</div>}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-3 pt-2.5" data-composer-files>
            {files.map((f) => {
              const Icon = KIND_ICON[f.kind];
              const pending = !!upload && !f.url && !f.failed;
              return f.preview ? (
                <span key={f.key} className="group relative h-12 w-12 overflow-hidden rounded-lg border border-[var(--shell-border,#eaecf0)] bg-[var(--shell-hover,#f9fafb)]" title={f.failed ? `${f.name} did not upload` : f.name}>
                  <img src={f.preview} alt={f.name} className={cn('h-full w-full object-cover', pending && 'opacity-50')} />
                  {pending && <Loader2 className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 animate-spin text-[#7a1fbf]" />}
                  {f.failed && <span className="absolute inset-x-0 bottom-0 bg-[#d92d20] text-center text-[9px] font-semibold text-white">failed</span>}
                  <button type="button" onClick={() => drop(f.key)} aria-label={`Remove ${f.name}`} className="absolute right-0.5 top-0.5 rounded-full bg-white/90 p-0.5 text-[#344054] opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"><X className="h-3 w-3" /></button>
                </span>
              ) : (
                <span key={f.key} className={cn('inline-flex max-w-[220px] items-center gap-1.5 rounded-lg border px-2 py-1 text-[12px]', f.failed ? 'border-[#fda29b] bg-[#fef3f2] text-[#b42318]' : 'border-[var(--shell-border,#eaecf0)] bg-[var(--shell-hover,#f9fafb)] text-[var(--shell-text,#344054)]')} title={f.name}>
                  {pending ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> : <Icon className="h-3.5 w-3.5 shrink-0" />}
                  <span className="truncate">{f.name}</span>
                  <button type="button" onClick={() => drop(f.key)} aria-label={`Remove ${f.name}`} className="shrink-0 rounded p-0.5 hover:bg-black/5"><X className="h-3 w-3" /></button>
                </span>
              );
            })}
          </div>
        )}
        <textarea
          ref={box}
          value={text}
          rows={1}
          disabled={disabled}
          aria-label={`Message ${agentName}`}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(); } }}
          onPaste={(e) => { const fs = Array.from(e.clipboardData?.files || []); if (fs.length && allowFiles) { e.preventDefault(); void addFiles(fs); } }}
          placeholder={dictating ? 'Listening' : busy && !sendWhileBusy ? `${agentName} is working` : placeholder || `Message ${agentName}`}
          className={cn('block w-full resize-none bg-transparent px-3.5 text-[var(--shell-text,#101828)] outline-none placeholder:text-[var(--shell-placeholder,#98a2b3)] disabled:opacity-60',
            hero ? 'pt-3.5 text-[16px] leading-6' : 'pt-2.5 text-[14px] leading-[1.45]')}
        />
        <div className="flex items-center gap-0.5 px-1.5 pb-1.5 pt-1">
          {allowFiles && (
            <input ref={picker} type="file" multiple accept={accept} className="hidden" data-composer-picker
              onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; void addFiles(fs); }} />
          )}
          {allowFiles && (
            <button type="button" onClick={() => picker.current?.click()} aria-label="Attach files" title="Attach files: PDF, pictures, video, documents" className={iconBtn} disabled={disabled}><Paperclip className="h-4 w-4" /></button>
          )}
          {menu.length > 0 && (
            <div className="relative">
              <button type="button" onClick={() => setPlus((o) => !o)} aria-expanded={plus} aria-label="More" title="More" className={cn(iconBtn, plus && 'bg-[var(--shell-hover,#f2f4f7)]')} disabled={disabled}><Plus className="h-4 w-4" /></button>
              {plus && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setPlus(false)} />
                  <div role="menu" className="absolute bottom-10 left-0 z-50 w-64 overflow-hidden rounded-xl border border-[var(--shell-border,#eaecf0)] bg-[var(--shell-surface,#fff)] py-1 shadow-[0_8px_24px_rgba(16,24,40,0.14)]">
                    {menu.map((m) => (
                      <button key={m.label} type="button" role="menuitem" onClick={() => { setPlus(false); m.run(); }} className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-[var(--shell-hover,#f9fafb)]">
                        <m.icon className="mt-0.5 h-4 w-4 shrink-0 text-[#7a1fbf]" />
                        <span><span className="block text-[13px] font-medium text-[var(--shell-text,#101828)]">{m.label}</span>{m.hint && <span className="block text-[11.5px] text-[var(--shell-muted,#667085)]">{m.hint}</span>}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {notice && <span className="ml-1 min-w-0 truncate text-[11.5px] text-[#b42318]" role="status">{notice}</span>}
          {!notice && failed && <span className="ml-1 min-w-0 truncate text-[11.5px] text-[#b42318]" role="status">A file did not upload: remove it to send.</span>}
          <span className="flex-1" />
          {canDictate && (
            <button type="button" onClick={toggleDictation} aria-pressed={dictating} aria-label={dictating ? 'Stop dictating' : 'Dictate'} title={dictating ? 'Stop dictating' : 'Dictate: speak and it is typed'} disabled={disabled}
              className={cn(iconBtn, dictating && '!bg-[#fee4e2] !text-[#d92d20]')}>
              {dictating ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          )}
          {call && call.available !== false && (
            <button type="button" onClick={call.start} aria-label={`Call ${agentName}`} title={`Call ${agentName}: talk instead of typing`} disabled={disabled} className={iconBtn} data-composer-call>
              <Phone className="h-4 w-4" />
            </button>
          )}
          {busy && onStop ? (
            <button type="button" onClick={onStop} aria-label="Stop" title="Stop" className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--shell-text,#101828)] text-[var(--shell-surface,#fff)]"><Square className="h-3.5 w-3.5" /></button>
          ) : (
            <button type="button" onClick={() => void send()} disabled={!canSend} aria-label="Send" title={uploading ? 'Uploading the files' : 'Send (Enter)'}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#be43ff] text-white transition-colors hover:bg-[#a830e0] disabled:bg-[var(--shell-disabled,#e4e7ec)] disabled:text-[var(--shell-placeholder,#98a2b3)]">
              {busy && !sendWhileBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
